import { roundCurrency } from '@bill-alps/backend/shared/finance/money';
import { tsToYearMonth } from '../../bills/lib/vat-declaration';

export const MAX_NAMED_PIE_SLICES = 8;

/**
 * Mirrors the `MAX_MONTHS` cap of `features/bills/exchangeRates.getRatesForMonths`,
 * which silently truncates the requested month list. Callers must not ask for
 * more months than this — bills in the dropped months find no rate and are
 * reported as excluded instead.
 */
export const MAX_RATE_MONTHS = 24;

export interface PieSlice {
  key: string;
  label: string;
  value: number;
}

/** Row shape of `exchangeRates.getRatesForMonths` (CHF per one unit). */
export interface PieRateEntry {
  yearMonth: string;
  currency: string;
  ratePerUnit: number;
}

export interface ClientBillPieResult {
  slices: PieSlice[];
  /** Bills dropped because no BAZG rate was cached for their month. */
  excludedBillCount: number;
}

interface ClientBillPieInput<ClientId extends string> {
  clientId: ClientId;
  status: string;
  issueDate: number;
  currency: string;
  total: number;
}

interface EmployeePayslipPieInput<EmployeeId extends string> {
  employeeId: EmployeeId;
  year: number;
  month: number;
  totalGross: number;
}

interface RankedPieOptions<Id extends string> {
  labels: ReadonlyMap<Id, string>;
  otherLabel: string;
  language: string;
  maxNamedSlices?: number;
}

function rankPieSlices<Id extends string>(
  totals: ReadonlyMap<Id, number>,
  {
    labels,
    otherLabel,
    language,
    maxNamedSlices = MAX_NAMED_PIE_SLICES,
  }: RankedPieOptions<Id>,
): PieSlice[] {
  const collator = new Intl.Collator(language);
  const ranked = [...totals.entries()]
    .filter(([, value]) => value > 0)
    .map(([key, value]) => ({
      key,
      label: labels.get(key) ?? '—',
      value,
    }))
    .sort(
      (a, b) =>
        b.value - a.value ||
        collator.compare(a.label, b.label) ||
        collator.compare(a.key, b.key),
    );

  if (ranked.length <= maxNamedSlices) return ranked;

  const named = ranked.slice(0, maxNamedSlices);
  const otherValue = ranked
    .slice(maxNamedSlices)
    .reduce((sum, slice) => sum + slice.value, 0);

  return [...named, { key: '__other__', label: otherLabel, value: otherValue }];
}

/**
 * Invoice totals per client over a date range.
 *
 * `currency` is an ISO code to filter on, or `null` to take every currency at
 * once — foreign amounts are then converted to CHF with the BAZG monthly
 * average of the bill's issue month (same method as the VAT declaration), and
 * bills whose month has no cached rate are excluded and counted.
 */
export function buildClientBillPieSeries<ClientId extends string>(args: {
  bills: readonly ClientBillPieInput<ClientId>[];
  clientLabels: ReadonlyMap<ClientId, string>;
  from: Date;
  to: Date;
  currency: string | null;
  rates?: readonly PieRateEntry[];
  otherLabel: string;
  language: string;
}): ClientBillPieResult {
  const totals = new Map<ClientId, number>();
  const allCurrencies = args.currency === null;
  const rateByKey = new Map(
    (args.rates ?? []).map((rate) => [
      `${rate.currency}|${rate.yearMonth}`,
      rate.ratePerUnit,
    ]),
  );
  let excludedBillCount = 0;

  for (const bill of args.bills) {
    if (bill.status === 'draft' || bill.status === 'cancelled') continue;
    if (!allCurrencies && bill.currency !== args.currency) continue;

    const issuedAt = new Date(bill.issueDate);
    if (issuedAt < args.from || issuedAt > args.to) continue;

    let fx = 1;
    if (allCurrencies && bill.currency !== 'CHF') {
      const rate = rateByKey.get(
        `${bill.currency}|${tsToYearMonth(bill.issueDate)}`,
      );
      if (rate === undefined) {
        excludedBillCount += 1;
        continue;
      }
      fx = rate;
    }

    totals.set(
      bill.clientId,
      (totals.get(bill.clientId) ?? 0) + roundCurrency(bill.total * fx),
    );
  }

  return {
    slices: rankPieSlices(totals, {
      labels: args.clientLabels,
      otherLabel: args.otherLabel,
      language: args.language,
    }),
    excludedBillCount,
  };
}

export function buildEmployeePayslipPieSeries<EmployeeId extends string>(args: {
  payslips: readonly EmployeePayslipPieInput<EmployeeId>[];
  employeeLabels: ReadonlyMap<EmployeeId, string>;
  year: number;
  month?: number;
  otherLabel: string;
  language: string;
}): PieSlice[] {
  const totals = new Map<EmployeeId, number>();

  for (const payslip of args.payslips) {
    if (payslip.year !== args.year) continue;
    if (args.month !== undefined && payslip.month !== args.month) continue;

    totals.set(
      payslip.employeeId,
      (totals.get(payslip.employeeId) ?? 0) + payslip.totalGross,
    );
  }

  return rankPieSlices(totals, {
    labels: args.employeeLabels,
    otherLabel: args.otherLabel,
    language: args.language,
  });
}
